import { zodResolver } from '@hookform/resolvers/zod';
import { type GetStartedInput, getStartedSchema } from '@sea-snag/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@sea-snag/ui';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { Mail } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { SeaSnagLogo } from '../components/sea-snag-logo';
import { useUser } from '../hooks/use-auth';
import { authApi } from '../lib/api-client';
import { pickInitialOrg } from '../stores/org-store';

export const Route = createFileRoute('/get-started')({
  component: GetStartedPage,
});

function GetStartedPage() {
  const { isAuthenticated, user } = useUser();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const startCooldown = useCallback(() => {
    setCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => clearInterval(cooldownRef.current);
  }, []);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<GetStartedInput>({
    resolver: zodResolver(getStartedSchema),
  });

  if (isAuthenticated && user) {
    const orgs = (user.organizations ?? []).map((o) => ({
      pid: o.id,
      name: o.name,
      slug: o.slug,
      setupCompletedAt: o.setupCompletedAt,
    }));
    const org = pickInitialOrg(orgs);
    if (org) {
      return <Navigate to="/$orgPid/dashboard" params={{ orgPid: org.pid }} />;
    }
  }

  const onSubmit = async (data: GetStartedInput) => {
    setError(null);
    try {
      await authApi.getStarted(data);
      setSentTo(data.email);
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Something went wrong. Please try again.');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await authApi.getStarted({ email: getValues('email') });
      startCooldown();
    } catch {
      // Silently fail on resend
    } finally {
      setResending(false);
    }
  };

  // Success state: email sent
  if (sentTo) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md animate-reveal-up">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <h2 className="mb-2 text-2xl font-bold">Check your email</h2>
            <p className="mb-1 text-muted-foreground">We sent a setup link to</p>
            <p className="mb-6 font-medium">{sentTo}</p>
            <p className="mb-4 text-sm text-muted-foreground">
              Click the link in the email to continue setting up your account. The link expires in
              24 hours.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
            >
              {resending
                ? 'Sending...'
                : cooldown > 0
                  ? `Resend in ${cooldown}s`
                  : "Didn't receive it? Resend"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Default state: email input
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="flex w-full max-w-md flex-col items-center">
        <SeaSnagLogo className="mb-10 size-20 animate-reveal-up" color />
        <Card className="w-full animate-reveal-up">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold">Get started with Sea Snag</CardTitle>
            <CardDescription>
              Enter your email and we&apos;ll send you a link to set up your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
              aria-label="Get started form"
            >
              {error && (
                <div
                  role="alert"
                  className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  autoFocus
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  {...register('email')}
                />
                {errors.email && (
                  <p id="email-error" className="text-sm text-destructive">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                {isSubmitting ? 'Sending link...' : 'Get Started'}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-primary underline-offset-4 hover:underline">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
